// https://chatgpt.com/c/66df904a-79e4-8008-8d00-fd0c7761253e

import { getSessionToken } from "@/services/sessionToken";
import { useEffect, useRef } from "react";
import { BASE_WS_URL } from "../../../constants";
import consumeV4WebSocketMessage from "../services/consumeV4WebSocketMessage";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

export type WebsocketRoomKind =
  | "organization"
  | "workspace"
  | "user"
  | "interaction"
  | "rms_entity"
  | "rms_view"
  | "agent_session"
  | "chat_conversation";

export interface WebsocketRoomOptions {
  // Also fire onReconnect when the socket opens for the FIRST time, for a
  // consumer that read a snapshot before subscribing. The server subscribes a
  // socket only after the upgrade, so an event pushed after the snapshot read
  // and before that is in neither; the first open is the consumer's cue to
  // re-read (#12187). A consumer that joins a room whose socket is already
  // open is not called: its snapshot starts after the open, so it has no gap.
  backfillOnFirstOpen?: boolean;
  // Called whenever the room's socket opens (the first open and every
  // reconnect), and at once when the consumer joins a room whose socket is
  // already open. The server adds a socket to the room before it accepts the
  // upgrade, so from this call on the room's pushes reach this consumer: a
  // consumer that starts something the server will stream back waits for it
  // rather than guessing how long a join takes (#12187).
  //
  // `firstOpen` is true on the first call since this consumer joined the room,
  // however the room got open (its first open, a cached open socket, or a
  // retry the consumer joined during), and false on every later call, each of
  // which follows a drop while the consumer was subscribed. A consumer whose
  // snapshot predates its join re-reads on the first; a reconnect means pushes
  // were lost while it was subscribed (#12187).
  //
  // `openedAt` is when the room's socket opened and `lastMessageAt` when it last
  // received a frame (either one null when unknown or none), on
  // `performance.now()`. A frame that lands while no consumer listens is not
  // delivered to anyone, so a consumer whose snapshot read was asked for after
  // the socket opened and after its last frame has missed nothing, and skips
  // its re-read (a return to a page whose socket stayed open, dashboard polish
  // requirement 15); any later frame means it may have.
  onOpen?: (info: {
    firstOpen: boolean;
    openedAt: number | null;
    lastMessageAt: number | null;
  }) => void;
}
interface RoomCallbacks {
  callback?: (message: unknown) => void;
  onReconnect?: () => void;
  options?: WebsocketRoomOptions;
}
// One consumer's membership of one room. A new one per join, so a consumer
// that moves to another room starts over with `opened` false.
interface Subscriber {
  callbacks: { current: RoomCallbacks };
  opened: boolean;
}
interface Room {
  socket: WebSocket | null;
  subscribers: Set<Subscriber>;
  retryCount: number;
  timer: ProductTimer | null;
}

// Not `WebSocket.OPEN`: the unit tests stub the global `WebSocket` with a fake
// that has no static constants. The value is fixed by the WebSocket spec.
const SOCKET_OPEN = 1;

// The first retry after a drop waits this long, doubling per failed attempt up
// to the cap. Exported so a test drives the retry by the product's own delay.
export const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

// When each socket opened, on `performance.now()`. Keyed by the socket, so a
// cached socket reused by a later join still says when it opened, and a closed
// one is dropped with it.
const socketOpenedAt = new WeakMap<WebSocket, number>();
// When each socket last received a frame, delivered or not, on the same clock.
const socketMessageAt = new WeakMap<WebSocket, number>();

function notifyOpen(subscriber: Subscriber, socket: WebSocket): void {
  const firstOpen = !subscriber.opened;
  subscriber.opened = true;
  try {
    subscriber.callbacks.current.options?.onOpen?.({
      firstOpen,
      openedAt: socketOpenedAt.get(socket) ?? null,
      lastMessageAt: socketMessageAt.get(socket) ?? null,
    });
  } catch (error) {
    console.error("WebSocket open listener error:", error);
  }
}

// A socket and its retry belong to the room, not whichever component last
// subscribed. Message and reconnect delivery both fan out to mounted consumers.
const rooms = new Map<string, Room>();

function connectRoom(wsUrl: string, room: Room): void {
  window.wsMap ??= {};
  let websocket = window.wsMap[wsUrl];
  if (!websocket) {
    const token = getSessionToken();
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
    websocket = new WebSocket(`${wsUrl}${tokenParam}`);
    window.wsMap[wsUrl] = websocket;
  }
  room.socket = websocket;

  websocket.onmessage = (event) => {
    socketMessageAt.set(websocket, performance.now());
    const hasListeners = [...room.subscribers].some((subscriber) =>
      Boolean(subscriber.callbacks.current.callback)
    );
    if (!hasListeners) {
      consumeV4WebSocketMessage(event, {});
      return;
    }
    let message: unknown;
    try {
      const parsed: unknown = JSON.parse(event.data);
      // The previous envelope reader treated null as malformed input.
      if (parsed === null) throw new TypeError("WebSocket message is null");
      // Preserve the legacy envelope and its falsy-payload fallback.
      message =
        parsed && typeof parsed === "object" && "message" in parsed
          ? parsed.message || parsed
          : parsed;
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
      message = event;
    }
    for (const subscriber of room.subscribers) {
      try {
        subscriber.callbacks.current.callback?.(message);
      } catch (error) {
        console.error("WebSocket listener error:", error);
      }
    }
  };
  websocket.onopen = () => {
    socketOpenedAt.set(websocket, performance.now());
    const wasReconnect = room.retryCount > 0;
    room.retryCount = 0;
    for (const subscriber of room.subscribers) {
      notifyOpen(subscriber, websocket);
      const { onReconnect, options } = subscriber.callbacks.current;
      if (!wasReconnect && !options?.backfillOnFirstOpen) continue;
      try {
        onReconnect?.();
      } catch (error) {
        console.error("WebSocket reconnect listener error:", error);
      }
    }
  };
  websocket.onclose = () => {
    // A late close from an older socket cannot evict its replacement.
    if (room.socket !== websocket) return;
    if (window.wsMap[wsUrl] === websocket) delete window.wsMap[wsUrl];
    room.socket = null;
    if (room.subscribers.size === 0) return;
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** room.retryCount,
      RECONNECT_MAX_DELAY_MS
    );
    room.retryCount += 1;
    room.timer = setProductTimeout(() => {
      room.timer = null;
      connectRoom(wsUrl, room);
    }, delay);
  };
}

export default function useWebsocketRoom(
  kind: WebsocketRoomKind,
  id: string | null,
  discriminator?: string | null,
  callback?: (message: unknown) => void,
  // Fired when the socket re-opens after a drop, and on its first open too
  // when `options.backfillOnFirstOpen` is set. Every mounted consumer can
  // backfill state missed while it was not subscribed.
  onReconnect?: () => void,
  options?: WebsocketRoomOptions
) {
  // Callback changes must not disconnect a room or reset its backoff.
  const callbacks = useRef<RoomCallbacks>({ callback, onReconnect, options });
  callbacks.current = { callback, onReconnect, options };

  useEffect(() => {
    if (!id) return;
    const wsUrl = `${BASE_WS_URL}/${kind}/${id}_${discriminator || "default"}`;
    let room = rooms.get(wsUrl);
    if (!room) {
      room = {
        socket: null,
        subscribers: new Set(),
        retryCount: 0,
        timer: null,
      };
      rooms.set(wsUrl, room);
    }
    const subscriber: Subscriber = { callbacks, opened: false };
    room.subscribers.add(subscriber);
    if (!room.socket && room.timer === null) connectRoom(wsUrl, room);
    // Joined a room already open (or reused a cached open socket): its open
    // event has passed, so this consumer is told now.
    if (room.socket?.readyState === SOCKET_OPEN)
      notifyOpen(subscriber, room.socket);

    return () => {
      room.subscribers.delete(subscriber);
      if (room.subscribers.size > 0) return;
      if (room.timer !== null) clearProductTimeout(room.timer);
      rooms.delete(wsUrl);
      // Preserve cached socket reuse between mounts. With no subscribers its
      // close handler removes the dead socket but schedules no retry.
    };
  }, [kind, id, discriminator]);
}
